import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatRetentionBound from '@UI/Pages/Administration/Retention/RetentionSettingsForm/Helpers/formatRetentionBound';
import { css } from '@emotion/react';
import * as React from 'react';
import { InputNumber, SelectButton } from '@verclam/ui-kit';

const CUSTOM_KEY = 'custom';

const groupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const customFieldStyle = css`
	max-width: 18rem;
`;

export type DurationPreset = {
	readonly label: string;
	readonly value: number;
};

type Props = {
	readonly name: string;
	readonly title: string;
	readonly description: string;
	readonly presets: readonly DurationPreset[];
	/** Plural unit shown in the custom field's label and help, e.g. "jours". */
	readonly unit: string;
	readonly minimum: number;
	readonly maximum: number;
	readonly value: number | null;
	readonly onChange: (value: number | null) => void;
	readonly errorMessage: string | undefined;
	readonly isValid: boolean;
};

/** Preset duration chips plus a "Personnalisée" chip that reveals an integer field. */
const DurationChoice: React.FC<Props> = ({
	name,
	title,
	description,
	presets,
	unit,
	minimum,
	maximum,
	value,
	onChange,
	errorMessage,
	isValid,
}) => {
	const [isCustom, setIsCustom] = React.useState(() => !presets.some((preset) => preset.value === value));

	const options = React.useMemo(
		() => [
			...presets.map((preset) => ({ label: preset.label, key: String(preset.value) })),
			{ label: 'Personnalisée', key: CUSTOM_KEY },
		],
		[presets],
	);

	const currentKey = isCustom ? CUSTOM_KEY : String(value);

	const handleChoice = React.useCallback(
		(choice: string | readonly string[] | null) => {
			if (choice === CUSTOM_KEY) {
				setIsCustom(true);
				return;
			}

			const preset = presets.find((candidate) => String(candidate.value) === choice);

			if (preset === undefined) {
				return;
			}

			setIsCustom(false);
			onChange(preset.value);
		},
		[presets, onChange],
	);

	return (
		<div css={groupStyle}>
			<h2 css={titleStyle}>{title}</h2>
			<p css={descriptionStyle}>{description}</p>
			<div>
				<SelectButton
					name={`${name}Presets`}
					label={title}
					isLabelHidden
					options={options}
					optionLabel="label"
					optionValue="key"
					value={currentKey}
					onChange={handleChoice}
					allowEmpty={false}
					size="sm"
				/>
			</div>
			<ConditionalLoader data={isCustom}>
				<div css={customFieldStyle}>
					<InputNumber
						name={name}
						label={`Durée personnalisée (${unit})`}
						subLabel={`Entre ${formatRetentionBound(minimum)} et ${formatRetentionBound(maximum)} ${unit}.`}
						required
						useGrouping={false}
						value={value}
						onChange={onChange}
						errorMessage={errorMessage}
						isFormFieldValid={isValid}
					/>
				</div>
			</ConditionalLoader>
		</div>
	);
};

export default DurationChoice;
